import React from 'react';
import { useTranslation } from 'react-i18next';
import { AdditionalFieldsPanelProps, FeiertageTimeData, TimeFieldValidationErrors } from './types';
import { DATENFORM_OPTIONS, LAND_OPTIONS } from './constants';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface ExtendedAdditionalFieldsPanelProps extends AdditionalFieldsPanelProps {
  validationErrors?: TimeFieldValidationErrors;
}

/**
 * Panel component for additional time information fields
 * Displays detailed settings when a checkbox is checked
 */
export const AdditionalFieldsPanel: React.FC<ExtendedAdditionalFieldsPanelProps> = ({
  fieldId,
  fieldData,
  isLoading,
  onDetailedCalcChange,
  onDatenformChange,
  onZeithorizontChange,
  onSkalierungChange,
  onSkalierungValueChange,
  onLandChange,
  validationErrors = {}
}) => {
  const { t } = useTranslation('training');

  const inputClasses = (hasError: boolean) => `w-full bg-card border rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
    hasError
      ? 'border-red-300 focus:ring-red-500/30 focus:border-red-400'
      : 'border-input focus:ring-ring/30 focus:border-ring'
  }`;

  const selectClasses = (hasError: boolean) => `w-full bg-card border rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 transition-shadow duration-150 ${
    hasError
      ? 'border-red-300 focus:ring-red-500/30 focus:border-red-400'
      : 'border-input focus:ring-ring/30 focus:border-ring'
  }`;

  const labelClasses = 'text-[10px] text-muted-foreground uppercase tracking-wide block mb-1';

  return (
    <div className="pt-2 border-t border-border space-y-2.5">
      {/* Detaillierte Berechnung */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={fieldData.detaillierteBerechnung}
            onChange={onDetailedCalcChange}
            className="w-3.5 h-3.5 rounded accent-primary"
            disabled={isLoading}
          />
          <span className="text-xs text-foreground">
            {t('timeInformation.fields.detaillierteBerechnung')}
          </span>
        </label>
        <InfoTooltip text={t('tooltips.zeitinformationen.zeitumstellung')} width="w-80" />
      </div>

      {/* Datenform */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label htmlFor={`time-info-datenform-${fieldId}`} className={labelClasses} style={{ marginBottom: 0 }}>
            {t('timeInformation.fields.datenform')}
          </label>
          <InfoTooltip text={t('tooltips.zeitinformationen.datenform')} width="w-72" align="start" />
        </div>
        <select
          id={`time-info-datenform-${fieldId}`}
          value={fieldData.datenform}
          onChange={onDatenformChange}
          className={selectClasses(!!validationErrors.datenform)}
          disabled={isLoading}
        >
          {DATENFORM_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.value === ''
                ? t('timeInformation.options.pleaseSelect')
                : option.value === 'Zeithorizont'
                  ? t('timeInformation.options.zeitHorizont')
                  : t('timeInformation.options.aktuelleZeit')}
            </option>
          ))}
        </select>
        {validationErrors.datenform && (
          <p className="text-red-500 text-[10px] mt-1">{validationErrors.datenform}</p>
        )}
      </div>

      {/* Zeithorizont */}
      {fieldData.datenform === 'Zeithorizont' && (
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className={labelClasses} id={`time-info-zeithorizont-label-${fieldId}`} style={{ marginBottom: 0 }}>
              {t('timeInformation.fields.zeithorizont')}
            </label>
            <InfoTooltip text={t('tooltips.zeitinformationen.zeithorizont')} width="w-80" align="start" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <input
                type="number"
                step="0.01"
                placeholder={`${t('timeInformation.fields.start')} [h]`}
                value={fieldData.zeithorizontStart}
                onChange={(e) => onZeithorizontChange('zeithorizontStart', e.target.value)}
                className={inputClasses(!!validationErrors.zeithorizontStart)}
                disabled={isLoading}
                aria-label={`${t('timeInformation.fields.zeithorizont')} – ${t('timeInformation.fields.start')} [h]`}
              />
              {validationErrors.zeithorizontStart && (
                <p className="text-red-500 text-[10px] mt-0.5">{validationErrors.zeithorizontStart}</p>
              )}
            </div>
            <div>
              <input
                type="number"
                step="0.01"
                placeholder={`${t('timeInformation.fields.end')} [h]`}
                value={fieldData.zeithorizontEnd}
                onChange={(e) => onZeithorizontChange('zeithorizontEnd', e.target.value)}
                className={inputClasses(!!validationErrors.zeithorizontEnd)}
                disabled={isLoading}
                aria-label={`${t('timeInformation.fields.zeithorizont')} – ${t('timeInformation.fields.end')} [h]`}
              />
              {validationErrors.zeithorizontEnd && (
                <p className="text-red-500 text-[10px] mt-0.5">{validationErrors.zeithorizontEnd}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skalierung */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label className={labelClasses} style={{ marginBottom: 0 }}>
            {t('timeInformation.fields.skalierung')}
          </label>
          <InfoTooltip text={t('tooltips.zeitinformationen.skalierung')} width="w-96" align="start" />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={fieldData.skalierung === 'ja'}
              onChange={onSkalierungChange}
              className="accent-primary"
              disabled={isLoading}
            />
            <span className="text-xs text-foreground">{t('common.yes', { ns: 'common' })}</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={fieldData.skalierung === 'nein'}
              onChange={onSkalierungChange}
              className="accent-primary"
              disabled={isLoading}
            />
            <span className="text-xs text-foreground">{t('common.no', { ns: 'common' })}</span>
          </label>
        </div>
      </div>

      {/* Skalierung Min/Max */}
      {fieldData.skalierung === 'ja' && (
        <div>
          <label className={labelClasses}>
            {t('timeInformation.minMax')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <input
                type="number"
                step="0.01"
                placeholder={t('timeInformation.fields.min')}
                value={fieldData.skalierungMin}
                onChange={(e) => onSkalierungValueChange('skalierungMin', e.target.value)}
                className={inputClasses(!!validationErrors.skalierungMin)}
                disabled={isLoading}
                aria-label={`${t('timeInformation.fields.skalierung')} – ${t('timeInformation.fields.min')}`}
              />
              {validationErrors.skalierungMin && (
                <p className="text-red-500 text-[10px] mt-0.5">{validationErrors.skalierungMin}</p>
              )}
            </div>
            <div>
              <input
                type="number"
                step="0.01"
                placeholder={t('timeInformation.fields.max')}
                value={fieldData.skalierungMax}
                onChange={(e) => onSkalierungValueChange('skalierungMax', e.target.value)}
                className={inputClasses(!!validationErrors.skalierungMax)}
                disabled={isLoading}
                aria-label={`${t('timeInformation.fields.skalierung')} – ${t('timeInformation.fields.max')}`}
              />
              {validationErrors.skalierungMax && (
                <p className="text-red-500 text-[10px] mt-0.5">{validationErrors.skalierungMax}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Land (only for feiertag) */}
      {fieldId === 'feiertag' && onLandChange && (
        <div>
          <label htmlFor={`time-info-land-${fieldId}`} className={labelClasses}>
            {t('timeInformation.fields.land')}
          </label>
          <select
            id={`time-info-land-${fieldId}`}
            value={(fieldData as FeiertageTimeData).land}
            onChange={onLandChange}
            className={selectClasses(!!validationErrors.land)}
            disabled={isLoading}
          >
            <option value="">{t('timeInformation.options.pleaseSelect')}</option>
            {LAND_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
          {validationErrors.land && (
            <p className="text-red-500 text-[10px] mt-1">{validationErrors.land}</p>
          )}
        </div>
      )}

      {/* Feiertag info note */}
      {fieldId === 'feiertag' && (
        <p className="text-[10px] text-amber-600 bg-amber-50/60 rounded px-2 py-1.5 border border-amber-200/60">
          {t('timeInformation.feiertageHinweis')}
        </p>
      )}
    </div>
  );
};

export default AdditionalFieldsPanel;
